/**
 * S3 Direct Upload Utility with Multipart Support
 * 
 * Handles direct uploads to S3 using:
 * - Single PUT Upload (for files < 50MB) - Direct browser upload with presigned URL
 * - Client-Side Direct Multipart Upload (for files >= 50MB) - Browser chunks file and uploads directly to S3
 * 
 * Features:
 * - Automatic chunking and parallel uploads (client-side)
 * - Progress tracking (0-100%)
 * - Automatic retry on chunk failures
 * - Abort functionality with cleanup
 * - Memory efficient (uses File.slice(), doesn't load entire file into memory)
 */

/**
 * Initialize upload session (backend decides: multipart or single)
 * 
 * @param {string} filename - Original filename
 * @param {string} contentType - MIME type (e.g., 'video/mp4')
 * @param {number} fileSize - File size in bytes
 * @param {string} userId - User ID for organizing files
 * @returns {Promise<{ uploadId?: string, s3Key: string, uploadUrl?: string, useMultipart: boolean }>}
 */
async function initializeUpload(filename, contentType, fileSize, userId) {
    const nativeFetch = window.__nativeFetch || window.fetch;

    const response = await nativeFetch('/api/upload/multipart-init', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-Clerk-User-Id': userId,
        },
        body: JSON.stringify({
            filename,
            contentType,
            fileSize,
        }),
    });

    if (!response.ok) {
        const error = await response.text();
        throw new Error(`Failed to initialize upload: ${error}`);
    }

    return response.json();
}

/**
 * Upload file directly to S3 using presigned URL (single PUT - for small files)
 * 
 * @param {string} uploadUrl - Presigned S3 URL
 * @param {Blob|File} file - File to upload
 * @param {string} contentType - MIME type
 * @param {Function} onProgress - Progress callback (percent: 0-100)
 * @returns {Promise<void>}
 */
async function uploadToS3Single(uploadUrl, file, contentType, onProgress) {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        // Calculate timeout based on file size
        const fileSizeMB = file.size / (1024 * 1024);
        let timeoutMs = 5 * 60 * 1000; // Default 5 minutes
        if (fileSizeMB > 300) {
            timeoutMs = 45 * 60 * 1000; // 45 minutes for very large files
        } else if (fileSizeMB > 150) {
            timeoutMs = 30 * 60 * 1000; // 30 minutes for large files
        } else if (fileSizeMB > 50) {
            timeoutMs = 15 * 60 * 1000; // 15 minutes for medium files
        }

        xhr.timeout = timeoutMs;

        xhr.upload.addEventListener('progress', (event) => {
            if (event.lengthComputable && onProgress) {
                const percent = Math.round((event.loaded / event.total) * 100);
                onProgress(percent);
            }
        });

        xhr.addEventListener('load', () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                resolve();
            } else {
                reject(new Error(`S3 upload failed: ${xhr.status} ${xhr.statusText}`));
            }
        });

        xhr.addEventListener('error', () => {
            reject(new Error('S3 upload failed: Network error'));
        });

        xhr.addEventListener('timeout', () => {
            reject(new Error(`S3 upload timeout after ${timeoutMs / 1000 / 60} minutes. The file may be too large or your connection is too slow.`));
        });

        xhr.addEventListener('abort', () => {
            reject(new Error('S3 upload aborted'));
        });

        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Content-Type', contentType);
        xhr.send(file);
    });
}

/**
 * Split file into chunks for multipart upload
 * 
 * @param {File} file - File to chunk
 * @param {number} chunkSize - Size of each chunk in bytes (default: 10MB)
 * @returns {Array<{chunk: Blob, partNumber: number, start: number, end: number}>}
 */
function splitFileIntoChunks(file, chunkSize = 10 * 1024 * 1024) {
    const chunks = [];
    let partNumber = 1;
    let start = 0;

    while (start < file.size) {
        const end = Math.min(start + chunkSize, file.size);
        const chunk = file.slice(start, end);
        chunks.push({
            chunk,
            partNumber,
            start,
            end,
        });
        partNumber++;
        start = end;
    }

    return chunks;
}

/**
 * Get presigned URL for a specific part in multipart upload
 * 
 * @param {string} uploadId - Multipart upload ID
 * @param {string} s3Key - S3 object key
 * @param {number} partNumber - Part number (1-indexed)
 * @param {string} userId - User ID
 * @returns {Promise<{presignedUrl: string, partNumber: number}>}
 */
async function getPresignedUrlForPart(uploadId, s3Key, partNumber, userId) {
    const nativeFetch = window.__nativeFetch || window.fetch;

    const response = await nativeFetch('/api/upload/multipart-presigned-url', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-Clerk-User-Id': userId,
        },
        body: JSON.stringify({
            uploadId,
            s3Key,
            partNumber,
        }),
    });

    if (!response.ok) {
        const error = await response.text();
        throw new Error(`Failed to get presigned URL for part ${partNumber}: ${error}`);
    }

    return response.json();
}

/**
 * Upload a single part to S3 using presigned URL
 * 
 * @param {Blob} chunk - Chunk to upload
 * @param {string} presignedUrl - Presigned URL for the part
 * @param {number} partNumber - Part number (for logging)
 * @param {number} maxRetries - Maximum number of retry attempts (default: 3)
 * @param {AbortSignal} abortSignal - Optional abort signal
 * @returns {Promise<string>} ETag from S3 response
 */
async function uploadPartToS3(chunk, presignedUrl, partNumber, maxRetries = 3, abortSignal = null) {
    let lastError;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
        // Check abort signal before retry
        if (abortSignal && abortSignal.aborted) {
            throw new Error('Upload aborted');
        }

        try {
            return await new Promise((resolve, reject) => {
                const xhr = new XMLHttpRequest();

                // Handle abort signal
                if (abortSignal) {
                    abortSignal.addEventListener('abort', () => {
                        xhr.abort();
                        reject(new Error('Upload aborted'));
                    });
                }

                xhr.upload.addEventListener('progress', (event) => {
                    // Progress tracking is handled at higher level
                });

                xhr.addEventListener('load', () => {
                    if (xhr.status >= 200 && xhr.status < 300) {
                        // Extract ETag from response headers
                        const etag = xhr.getResponseHeader('ETag');
                        if (!etag) {
                            reject(new Error(`No ETag in response for part ${partNumber}`));
                            return;
                        }
                        // Remove quotes from ETag if present
                        const cleanEtag = etag.replace(/^"|"$/g, '');
                        resolve(cleanEtag);
                    } else {
                        reject(new Error(`S3 upload failed for part ${partNumber}: ${xhr.status} ${xhr.statusText}`));
                    }
                });

                xhr.addEventListener('error', () => {
                    reject(new Error(`Network error uploading part ${partNumber}`));
                });

                xhr.addEventListener('timeout', () => {
                    reject(new Error(`Timeout uploading part ${partNumber}`));
                });

                xhr.addEventListener('abort', () => {
                    reject(new Error(`Upload aborted for part ${partNumber}`));
                });

                // Set timeout: 5 minutes per chunk (should be enough for 10MB)
                xhr.timeout = 5 * 60 * 1000;

                xhr.open('PUT', presignedUrl);
                // Don't set Content-Type - S3 will determine it from the presigned URL
                xhr.send(chunk);
            });
        } catch (error) {
            lastError = error;
            console.warn(`[S3Upload] Part ${partNumber} attempt ${attempt}/${maxRetries} failed:`, error.message);

            if (attempt < maxRetries) {
                // Exponential backoff: 1s, 2s, 4s
                const waitMs = Math.pow(2, attempt - 1) * 1000;
                console.log(`[S3Upload] Waiting ${waitMs / 1000}s before retry for part ${partNumber}...`);
                await new Promise(resolve => setTimeout(resolve, waitMs));
            }
        }
    }

    throw new Error(`Failed to upload part ${partNumber} after ${maxRetries} attempts. Last error: ${lastError?.message || 'Unknown error'}`);
}

/**
 * Upload all parts in parallel with queue management
 * 
 * @param {Array<{chunk: Blob, partNumber: number, start: number, end: number}>} chunks - Array of chunks
 * @param {string} uploadId - Multipart upload ID
 * @param {string} s3Key - S3 object key
 * @param {string} userId - User ID
 * @param {Function} onProgress - Progress callback ({ stage: string, percent: number, loaded: number, total: number })
 * @param {AbortSignal} abortSignal - Abort signal for cancellation
 * @param {number} queueSize - Maximum concurrent uploads (default: 4)
 * @returns {Promise<Array<{PartNumber: number, ETag: string}>>} Array of parts with ETags
 */
async function uploadAllParts(chunks, uploadId, s3Key, userId, onProgress, abortSignal, queueSize = 4) {
    const totalBytes = chunks.reduce((sum, chunk) => sum + chunk.chunk.size, 0);
    let uploadedBytes = 0;
    const parts = [];
    const errors = [];

    // Queue system for parallel uploads
    const queue = [];
    let activeUploads = 0;
    let currentChunkIndex = 0;

    return new Promise((resolve, reject) => {
        // Check abort signal
        if (abortSignal && abortSignal.aborted) {
            reject(new Error('Upload aborted'));
            return;
        }

        const processNext = async () => {
            // Check abort signal
            if (abortSignal && abortSignal.aborted) {
                reject(new Error('Upload aborted'));
                return;
            }

            // If all chunks are processed
            if (currentChunkIndex >= chunks.length && activeUploads === 0) {
                if (errors.length > 0) {
                    reject(new Error(`Failed to upload ${errors.length} parts: ${errors[0].message}`));
                    return;
                }
                // Sort parts by PartNumber (required by S3)
                parts.sort((a, b) => a.PartNumber - b.PartNumber);
                resolve(parts);
                return;
            }

            // Start new uploads if we have capacity and chunks left
            while (activeUploads < queueSize && currentChunkIndex < chunks.length) {
                const chunkInfo = chunks[currentChunkIndex];
                currentChunkIndex++;
                activeUploads++;

                // Get presigned URL and upload part
                (async () => {
                    let percent = 0; // Initialize percent to avoid ReferenceError
                    try {
                        // Check abort signal before starting
                        if (abortSignal && abortSignal.aborted) {
                            throw new Error('Upload aborted');
                        }

                        // Get presigned URL
                        const { presignedUrl } = await getPresignedUrlForPart(
                            uploadId,
                            s3Key,
                            chunkInfo.partNumber,
                            userId
                        );

                        // Check abort signal again
                        if (abortSignal && abortSignal.aborted) {
                            throw new Error('Upload aborted');
                        }

                        // Upload part
                        const etag = await uploadPartToS3(
                            chunkInfo.chunk,
                            presignedUrl,
                            chunkInfo.partNumber,
                            3, // maxRetries
                            abortSignal
                        );

                        // Update progress
                        uploadedBytes += chunkInfo.chunk.size;
                        percent = Math.round((uploadedBytes / totalBytes) * 100);
                        
                        if (onProgress) {
                            onProgress({
                                stage: 'uploading',
                                percent,
                                loaded: uploadedBytes,
                                total: totalBytes,
                            });
                        }

                        // Store part info
                        parts.push({
                            PartNumber: chunkInfo.partNumber,
                            ETag: etag,
                        });

                        console.log(`[S3Upload] Part ${chunkInfo.partNumber}/${chunks.length} uploaded (${percent}%)`);
                    } catch (error) {
                        // Calculate percent even on error for logging
                        percent = totalBytes > 0 ? Math.round((uploadedBytes / totalBytes) * 100) : 0;
                        console.error(`[S3Upload] Failed to upload part ${chunkInfo.partNumber}/${chunks.length} (${percent}%):`, error);
                        errors.push(error);
                    } finally {
                        activeUploads--;
                        processNext();
                    }
                })();
            }
        };

        // Start processing
        processNext();
    });
}

/**
 * Complete multipart upload
 * 
 * @param {string} uploadId - Multipart upload ID
 * @param {string} s3Key - S3 object key
 * @param {Array<{PartNumber: number, ETag: string}>} parts - Array of parts with ETags
 * @param {string} userId - User ID
 * @returns {Promise<{s3Key: string}>}
 */
async function completeMultipartUpload(uploadId, s3Key, parts, userId) {
    const nativeFetch = window.__nativeFetch || window.fetch;

    const response = await nativeFetch('/api/upload/multipart-complete', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-Clerk-User-Id': userId,
        },
        body: JSON.stringify({
            uploadId,
            s3Key,
            parts: parts.map(part => ({
                partNumber: part.PartNumber,
                etag: part.ETag,
            })),
        }),
    });

    if (!response.ok) {
        const error = await response.text();
        throw new Error(`Failed to complete multipart upload: ${error}`);
    }

    const result = await response.json();
    return { s3Key: result.s3Key };
}

/**
 * Upload large file using client-side direct multipart upload
 * 
 * @param {File} file - File to upload
 * @param {string} filename - Original filename
 * @param {string} contentType - MIME type
 * @param {string} uploadId - Multipart upload ID
 * @param {string} s3Key - S3 object key
 * @param {string} userId - User ID
 * @param {Function} onProgress - Progress callback ({ stage: string, percent: number, loaded: number, total: number })
 * @param {AbortSignal} abortSignal - Abort signal for cancellation
 * @returns {Promise<{s3Key: string}>}
 */
async function uploadMultipartDirect(file, filename, contentType, uploadId, s3Key, userId, onProgress, abortSignal) {
    console.log('[S3Upload] Starting client-side direct multipart upload');

    // Step 1: Split file into chunks
    const chunks = splitFileIntoChunks(file, 10 * 1024 * 1024); // 10MB chunks
    console.log(`[S3Upload] Split file into ${chunks.length} chunks`);

    // Step 2: Upload all parts in parallel
    if (onProgress) {
        onProgress({ stage: 'uploading', percent: 0, loaded: 0, total: file.size });
    }

    const parts = await uploadAllParts(
        chunks,
        uploadId,
        s3Key,
        userId,
        onProgress,
        abortSignal,
        4 // 4 concurrent uploads
    );

    console.log(`[S3Upload] All ${parts.length} parts uploaded successfully`);

    // Step 3: Complete multipart upload
    if (onProgress) {
        onProgress({ stage: 'completing', percent: 95 });
    }

    const result = await completeMultipartUpload(uploadId, s3Key, parts, userId);

    console.log('[S3Upload] Multipart upload completed');

    return result;
}

/**
 * Upload large file via backend proxy (uses multipart upload with SDK)
 * DEPRECATED: Use uploadMultipartDirect instead
 * 
 * @param {File|Blob} file - File to upload
 * @param {string} filename - Original filename
 * @param {string} contentType - MIME type
 * @param {string} userId - User ID
 * @param {Function} onProgress - Progress callback ({ stage: string, percent: number })
 * @param {AbortSignal} abortSignal - Abort signal for cancellation
 * @returns {Promise<{ s3Key: string }>}
 */
async function uploadViaBackendProxy(file, filename, contentType, userId, onProgress, abortSignal) {
    const nativeFetch = window.__nativeFetch || window.fetch;

    // Create FormData
    const formData = new FormData();
    formData.append('video', file);
    formData.append('filename', filename);
    formData.append('contentType', contentType);

    // Create AbortController if not provided
    const controller = abortSignal ? null : new AbortController();
    const signal = abortSignal || controller.signal;

    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        // Handle abort
        if (signal) {
            signal.addEventListener('abort', () => {
                xhr.abort();
                reject(new Error('Upload aborted'));
            });
        }

        // Track upload progress
        xhr.upload.addEventListener('progress', (event) => {
            if (event.lengthComputable && onProgress) {
                const percent = Math.round((event.loaded / event.total) * 100);
                onProgress({
                    stage: 'uploading',
                    percent,
                    loaded: event.loaded,
                    total: event.total,
                });
            }
        });

        xhr.addEventListener('load', () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                try {
                    const response = JSON.parse(xhr.responseText);
                    resolve({ s3Key: response.s3Key });
                } catch (error) {
                    console.error('[S3Upload] Failed to parse server response:', xhr.responseText);
                    reject(new Error('Failed to parse server response'));
                }
            } else {
                let errorMessage = `Upload failed: ${xhr.status} ${xhr.statusText}`;
                let errorDetails = null;
                try {
                    const error = JSON.parse(xhr.responseText);
                    errorMessage = error.error || error.message || errorMessage;
                    errorDetails = error.details || error.message;
                } catch (e) {
                    // If response is not JSON, use raw text
                    const responseText = xhr.responseText || '';
                    if (responseText) {
                        errorMessage = responseText.substring(0, 200);
                    }
                }
                console.error('[S3Upload] Upload failed:', {
                    status: xhr.status,
                    statusText: xhr.statusText,
                    error: errorMessage,
                    details: errorDetails,
                    response: xhr.responseText?.substring(0, 500)
                });
                reject(new Error(errorMessage));
            }
        });

        xhr.addEventListener('error', (event) => {
            console.error('[S3Upload] Network error during upload:', event);
            reject(new Error('Upload failed: Network error'));
        });

        xhr.addEventListener('abort', () => {
            reject(new Error('Upload aborted'));
        });

        xhr.open('POST', '/api/upload/multipart-proxy');
        xhr.setRequestHeader('X-Clerk-User-Id', userId);
        // Don't set Content-Type header - let browser set it with boundary for multipart/form-data
        
        console.log('[S3Upload] Starting backend proxy upload:', {
            filename,
            contentType,
            fileSize: file.size,
            userId
        });
        
        xhr.send(formData);
    });
}

/**
 * Abort multipart upload (cleanup orphaned parts)
 * 
 * @param {string} s3Key - S3 object key
 * @param {string} uploadId - Multipart upload ID
 * @param {string} userId - User ID
 * @returns {Promise<void>}
 */
async function abortMultipartUpload(s3Key, uploadId, userId) {
    const nativeFetch = window.__nativeFetch || window.fetch;

    const response = await nativeFetch('/api/upload/multipart-abort', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-Clerk-User-Id': userId,
        },
        body: JSON.stringify({
            s3Key,
            uploadId,
        }),
    });

    if (!response.ok) {
        const error = await response.text();
        console.warn(`[S3Upload] Failed to abort multipart upload: ${error}`);
    }
}

/**
 * Complete upload workflow: initialize and upload
 * 
 * @param {Blob|File} file - File to upload
 * @param {string} filename - Original filename
 * @param {string} userId - User ID
 * @param {Function} onProgress - Progress callback ({ stage: string, percent: number, loaded?: number, total?: number })
 * @param {AbortSignal} abortSignal - Optional abort signal for cancellation
 * @returns {Promise<{ s3Key: string, uploadId?: string }>}
 */
export async function uploadVideoToS3(file, filename, userId, onProgress, abortSignal) {
    let uploadId = null;
    let s3Key = null;
    let useMultipart = false;

    try {
        // Step 1: Initialize upload (backend decides: multipart or single)
        if (onProgress) onProgress({ stage: 'preparing', percent: 0 });

        const initResult = await initializeUpload(
            filename,
            'video/mp4',
            file.size,
            userId
        );

        s3Key = initResult.s3Key;
        useMultipart = initResult.useMultipart || false;
        uploadId = initResult.uploadId || null;

        console.log(`[S3Upload] Initialized ${useMultipart ? 'multipart' : 'single'} upload: ${s3Key}`);

        // Step 2: Upload to S3
        if (onProgress) onProgress({ stage: 'uploading', percent: 0 });

        if (useMultipart) {
            // Large file: Use client-side direct multipart upload
            console.log('[S3Upload] Using client-side direct multipart upload');
            const result = await uploadMultipartDirect(
                file,
                filename,
                'video/mp4',
                uploadId,
                s3Key,
                userId,
                (progress) => {
                    if (onProgress) {
                        onProgress({
                            stage: progress.stage || 'uploading',
                            percent: progress.percent || 0,
                            loaded: progress.loaded,
                            total: progress.total,
                        });
                    }
                },
                abortSignal
            );
            s3Key = result.s3Key;
        } else {
            // Small file: Direct PUT upload with presigned URL
            const { uploadUrl } = initResult;
            if (!uploadUrl) {
                throw new Error('Upload URL not provided');
            }

            console.log('[S3Upload] Using direct PUT upload with presigned URL');
            await uploadToS3Single(uploadUrl, file, 'video/mp4', (percent) => {
                if (onProgress) {
                    onProgress({
                        stage: 'uploading',
                        percent,
                    });
                }
            });
        }

        console.log(`[S3Upload] Upload complete: ${s3Key}`);

        return { s3Key, uploadId };
    } catch (error) {
        // Cleanup on error: abort multipart upload if it was started
        if (useMultipart && uploadId && s3Key) {
            console.warn('[S3Upload] Aborting multipart upload due to error');
            try {
                await abortMultipartUpload(s3Key, uploadId, userId);
            } catch (abortError) {
                console.error('[S3Upload] Failed to abort multipart upload:', abortError);
            }
        }

        // Handle abort signal
        if (abortSignal && abortSignal.aborted) {
            throw new Error('Upload aborted by user');
        }

        throw error;
    }
}

/**
 * Legacy function: Get presigned URL (for backward compatibility)
 * 
 * @param {string} filename - Original filename
 * @param {string} contentType - MIME type (e.g., 'video/mp4')
 * @param {number} fileSize - File size in bytes
 * @param {string} userId - User ID for organizing files
 * @returns {Promise<{ uploadUrl: string, s3Key: string }>}
 */
export async function getPresignedUploadUrl(filename, contentType, fileSize, userId) {
    const nativeFetch = window.__nativeFetch || window.fetch;

    const response = await nativeFetch('/api/upload/presigned-url', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-Clerk-User-Id': userId,
        },
        body: JSON.stringify({
            filename,
            contentType,
            fileSize,
        }),
    });

    if (!response.ok) {
        const error = await response.text();
        throw new Error(`Failed to get presigned URL: ${error}`);
    }

    return response.json();
}

/**
 * Legacy function: Upload to S3 with retry (for backward compatibility)
 * 
 * @param {string} uploadUrl - Presigned S3 URL
 * @param {Blob|File} file - File to upload
 * @param {string} contentType - MIME type
 * @param {Function} onProgress - Progress callback (percent: 0-100)
 * @param {number} maxRetries - Maximum number of retry attempts (default: 3)
 * @returns {Promise<void>}
 */
export async function uploadToS3(uploadUrl, file, contentType, onProgress, maxRetries = 3) {
    let lastError;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
            console.log(`[S3Upload] Attempt ${attempt}/${maxRetries} for file of ${(file.size / (1024 * 1024)).toFixed(2)}MB`);
            
            await uploadToS3Single(uploadUrl, file, contentType, onProgress);
            
            // Success!
            if (attempt > 1) {
                console.log(`[S3Upload] Upload succeeded on attempt ${attempt}`);
            }
            return;
        } catch (error) {
            lastError = error;
            console.warn(`[S3Upload] Attempt ${attempt} failed:`, error.message);
            
            // Don't retry on abort (user cancelled)
            if (error.message.includes('aborted')) {
                throw error;
            }
            
            // For timeout errors, retry if we have attempts left
            if (attempt < maxRetries) {
                // Wait before retrying (exponential backoff: 2s, 4s, 8s...)
                const waitMs = Math.pow(2, attempt) * 1000;
                console.log(`[S3Upload] Waiting ${waitMs / 1000}s before retry...`);
                await new Promise(resolve => setTimeout(resolve, waitMs));
                
                // Reset progress for retry
                if (onProgress) onProgress(0);
            } else {
                // Last attempt failed - throw with detailed error
                throw error;
            }
        }
    }
    
    // All retries failed
    throw new Error(`Upload failed after ${maxRetries} attempts. Last error: ${lastError?.message || 'Unknown error'}`);
}
